import React from 'react';
import { format } from 'date-fns';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '@ui/dialog';
import { Button } from '@ui/button';
import { Separator } from '@ui/separator';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@ui/card';
import { Skeleton } from '@ui/skeleton';
import { ScrollArea } from '@ui/scroll-area';

import { getWhatsNewEntries, markWhatsNewViewed, WhatsNewEntry } from '@lib/api';
import { toProblem } from '@lib/api/base';
import { useWhatsNew } from '@components/whatsnew/WhatsNewContext';

// Helper function to format What's New description text
const formatDescription = (description: string) => {
  // Split by double line breaks to get paragraphs
  const paragraphs = description.split('\n\n');

  return paragraphs.map((paragraph, paragraphIndex) => {
    const lines = paragraph.split('\n').filter(line => line.trim());

    // Check if this paragraph contains bullet points
    const hasBulletPoints = lines.some(line => line.trim().startsWith('•'));

    if (hasBulletPoints) {
      // Separate bullet points from regular lines
      const regularLines: string[] = [];
      const bulletPoints: string[] = [];

      lines.forEach(line => {
        if (line.trim().startsWith('•')) {
          bulletPoints.push(line.trim().substring(1).trim());
        } else {
          regularLines.push(line.trim());
        }
      });

      return (
        <div key={paragraphIndex}>
          {regularLines.length > 0 && (
            <p className="mb-2">
              {regularLines.map((line, lineIndex) => (
                <React.Fragment key={lineIndex}>
                  {line}
                  {lineIndex < regularLines.length - 1 && <br />}
                </React.Fragment>
              ))}
            </p>
          )}
          {bulletPoints.length > 0 && (
            <ul className="list-disc pl-6 mb-4 space-y-1">
              {bulletPoints.map((bullet, bulletIndex) => (
                <li key={bulletIndex}>{bullet}</li>
              ))}
            </ul>
          )}
        </div>
      );
    } else {
      // Regular paragraph with line breaks
      return (
        <p key={paragraphIndex} className="mb-4 last:mb-0">
          {lines.map((line, lineIndex) => (
            <React.Fragment key={lineIndex}>
              {line}
              {lineIndex < lines.length - 1 && <br />}
            </React.Fragment>
          ))}
        </p>
      );
    }
  });
};

interface WhatsNewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function WhatsNewDialog({ open, onOpenChange }: WhatsNewDialogProps) {
  const [entries, setEntries] = React.useState<WhatsNewEntry[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const { decrementCount } = useWhatsNew();

  const fetchEntries = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getWhatsNewEntries();
      setEntries(result.entries);
    } catch (err) {
      const problem = toProblem(err);
      setError(problem.detail || problem.title || "Failed to load What's New entries");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDialogOpenChange = async (newOpen: boolean) => {
    if (newOpen && entries.length === 0) {
      // When opening the dialog and we have no entries yet, fetch them
      fetchEntries();
    }

    if (!newOpen && entries.some(entry => !entry.is_viewed)) {
      // If dialog is closing and there are unviewed entries, mark all as viewed
      try {
        const unviewedCount = entries.filter(entry => !entry.is_viewed).length;
        await markWhatsNewViewed([], true); // Mark all as viewed

        // Update local state to reflect this
        setEntries(entries.map(entry => ({ ...entry, is_viewed: true })));

        // Update the badge count immediately
        decrementCount(unviewedCount);
      } catch (err) {
        const problem = toProblem(err);
        console.error('Failed to mark entries as viewed:', problem);
      }
    }

    onOpenChange(newOpen);
  };

  React.useEffect(() => {
    if (open) {
      fetchEntries();
    }
  }, [open, fetchEntries]);

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh]" data-testid="whatsnew-dialog">
        <DialogHeader>
          <DialogTitle data-testid="whatsnew-dialog-title">What's New</DialogTitle>
        </DialogHeader>

        <ScrollArea className="pr-4 h-[60vh]">
          {loading ? (
            // Loading state
            <div className="space-y-4" data-testid="whatsnew-loading">
              {[1, 2, 3].map(i => (
                <Card key={i}>
                  <CardHeader>
                    <Skeleton className="h-6 w-1/3" />
                    <Skeleton className="h-4 w-1/4" />
                  </CardHeader>
                  <CardContent>
                    <Skeleton className="h-24 w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : error ? (
            // Error state
            <div className="p-4 text-center" data-testid="whatsnew-error">
              <p className="text-destructive">
                {error}. Please try again later or contact support if the problem persists.
              </p>
            </div>
          ) : entries.length === 0 ? (
            // Empty state
            <div className="p-4 text-center" data-testid="whatsnew-empty">
              <p className="text-muted-foreground">
                No update announcements are available at this time.
              </p>
            </div>
          ) : (
            // Entries list
            <div className="space-y-4" data-testid="whatsnew-entries">
              {entries.map(entry => (
                <Card key={entry.id} data-testid={`whatsnew-entry-${entry.id}`}>
                  <CardHeader>
                    <CardTitle>{entry.title}</CardTitle>
                    <CardDescription>
                      {format(new Date(entry.published_at), 'MMMM d, yyyy')}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="prose max-w-none">{formatDescription(entry.description)}</div>
                    {entry.image && (
                      <div className="mt-4">
                        <img
                          src={entry.image}
                          alt={entry.title}
                          className="rounded-md max-h-96 mx-auto"
                        />
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </ScrollArea>

        <Separator />

        <DialogFooter>
          <DialogClose asChild>
            <Button data-testid="whatsnew-close-button">Close</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
